import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, Output } from "@angular/core";
import { Subscription } from "rxjs";
import { CollaborationApi } from "../../../api/collaboration/collaboration.api";
import { PresenceContext, PresenceEntry, PresenceSnapshot } from "../../../model/collaboration/collaboration.model";
import { SocketData, SocketEventTypeEnum } from "../../../services/web-socket/contract/web-socket.contract";
import { WSUserService } from "../../../services/web-socket/ws-user.service";

/** Input that counts as activity for the idle rule (AC3: inactive collaborators leave the list). */
const INPUT_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"] as const;

/**
 * D3 NP-300: who has this Survey Product open now (AC1, AC3) and the trigger for live updates (AC5).
 *
 * The browser beats every few seconds (the server sets the interval). Each answer lists the people present and two
 * revisions; when a revision changes, `activityChanged` (anything about the product's collaboration or content) or
 * `contentChanged` (the Survey Product itself) fires so the page can re-read in place. A push over the existing user
 * WebSocket makes the next beat happen at once; without it the beat interval still delivers the change.
 * The session leaves when the page closes, the product changes, or the person is idle; a session that just stops
 * (closed laptop, lost network) expires on the server.
 */
@Component({
  selector: "app-collaboration-presence",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./collaboration-presence.component.html",
  styleUrls: ["./collaboration-presence.component.scss"],
})
export class CollaborationPresenceComponent implements OnChanges, OnDestroy {
  @Input() formGroupId?: number;
  @Input() context: PresenceContext = "Workspace";
  /** The product's collaboration changed but not the product itself (someone else's comment, decision, assignment...). */
  @Output() readonly activityChanged = new EventEmitter<void>();
  /** The Survey Product itself changed (details, status, attached questionnaires or documents); a page re-reads everything. */
  @Output() readonly contentChanged = new EventEmitter<void>();

  present: PresenceEntry[] = [];
  state: "starting" | "live" | "reconnecting" | "idle" | "unavailable" = "starting";
  /** Random per page instance: tells this tab from the person's other tabs. Never stored. */
  readonly sessionKey = CollaborationPresenceComponent.newSessionKey();

  private activeId?: number;
  private joined = false;
  private needsJoin = true;
  private timer?: ReturnType<typeof setTimeout>;
  private inFlight?: Subscription;
  private queued = false;
  private heartbeatMs = 10_000;
  private idleMs = 300_000;
  private lastInput = Date.now();
  private revisions?: { activity: string; content: string };
  /** Every beat gets a number; acknowledge() adopts the revisions of the first beat started after it (adoptFrom). */
  private beatSeq = 0;
  private adoptFrom = 0;
  private socket?: Subscription;
  private readonly onInput = (): void => {
    this.lastInput = Date.now();
    if (this.state === "idle") this.zone.run(() => this._resume());
  };

  constructor(
    private readonly api: CollaborationApi,
    private readonly ws: WSUserService,
    private readonly zone: NgZone,
  ) {
    // Input events fire constantly; listen outside Angular so they never trigger change detection.
    this.zone.runOutsideAngular(() => INPUT_EVENTS.forEach(type => document.addEventListener(type, this.onInput, { passive: true })));
  }

  static newSessionKey(): string {
    const bytes = new Uint8Array(18);
    crypto.getRandomValues(bytes);
    return "tab-" + Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
  }

  ngOnChanges(): void {
    const id = Number(this.formGroupId);
    if (this.activeId === id && this.state !== "unavailable") return;
    this._stop();
    this.present = [];
    this.revisions = undefined;
    this.adoptFrom = 0;
    if (!Number.isSafeInteger(id) || id <= 0) { this.state = "unavailable"; return; }
    this.activeId = id;
    this.state = "starting";
    this.lastInput = Date.now();
    this.needsJoin = true;
    this._listen();
    this._beat();
  }

  ngOnDestroy(): void {
    INPUT_EVENTS.forEach(type => document.removeEventListener(type, this.onInput));
    this._stop();
    this.socket?.unsubscribe();
  }

  /** Re-read now. */
  refresh(): void {
    if (this.activeId && (this.state === "live" || this.state === "starting" || this.state === "reconnecting")) this._beat();
  }

  /**
   * After this tab saved a change itself: re-read and take the new revisions without reporting them as someone else's.
   * Only a beat started after this call is adopted. A beat already in flight may or may not include the change, so its
   * revisions are ignored (neither reported nor taken as the new baseline); the adopting beat follows it at once.
   */
  acknowledge(): void {
    this.adoptFrom = this.beatSeq + 1;
    this.refresh();
  }

  @HostListener("document:visibilitychange")
  onVisibilityChange(): void {
    if (document.visibilityState !== "visible") return;
    this.lastInput = Date.now();
    if (this.state === "idle") this._resume();
    else this.refresh();
  }

  initials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return "?";
    return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase();
  }

  describe(person: PresenceEntry): string {
    return `${person.name}${person.isSelf ? " (you)" : ""}: ${person.activity === "Editing" ? "editing in Survey Builder" : "viewing"}`;
  }

  get statusText(): string {
    switch (this.state) {
      case "live": return `${this.present.length} ${this.present.length === 1 ? "person" : "people"} here now. Live updates on.`;
      case "reconnecting": return "Reconnecting. Live updates are paused.";
      case "idle": return "Paused while you are inactive.";
      default: return "Connecting…";
    }
  }

  trackPerson(_index: number, person: PresenceEntry): number {
    return person.userId;
  }

  private _beat(): void {
    const id = this.activeId;
    if (!id) return;
    if (this.inFlight) { this.queued = true; return; }
    clearTimeout(this.timer);
    if (Date.now() - this.lastInput >= this.idleMs) { this._goIdle(); return; }
    const joining = this.needsJoin;
    const seq = ++this.beatSeq;
    const subscription = this.api.presence(id, this.sessionKey, this.context, joining).subscribe({
      next: (snapshot: PresenceSnapshot) => {
        this.inFlight = undefined;
        if (id !== this.activeId || snapshot.formGroupId !== id) return;
        this.joined = true;
        this.needsJoin = false;
        this._apply(snapshot, seq);
        this._next();
      },
      error: (error: unknown) => {
        this.inFlight = undefined;
        if (id !== this.activeId) return;
        const status = error instanceof HttpErrorResponse ? error.status : 0;
        // No access (or Survey Builder for a non-Admin): show nothing and stop. The server decides; the page never guesses.
        if ([400, 401, 403, 404].includes(status)) { this.state = "unavailable"; this.present = []; return; }
        this.state = "reconnecting";
        this.needsJoin = true;
        this._next();
      },
    });
    if (!subscription.closed) this.inFlight = subscription;
  }

  private _apply(snapshot: PresenceSnapshot, seq: number): void {
    this.heartbeatMs = Math.min(60, Math.max(2, Number(snapshot.heartbeatSeconds) || 10)) * 1000;
    this.idleMs = Math.max(30, Number(snapshot.idleSeconds) || 300) * 1000;
    this.present = snapshot.present;
    this.state = "live";
    if (this.adoptFrom && seq < this.adoptFrom) return;
    const previous = this.revisions;
    this.revisions = { activity: snapshot.activityRevision, content: snapshot.contentRevision };
    if (!previous || this.adoptFrom) { this.adoptFrom = 0; return; }
    if (previous.content !== snapshot.contentRevision) this.contentChanged.emit();
    else if (previous.activity !== snapshot.activityRevision) this.activityChanged.emit();
  }

  private _next(): void {
    if (!this.socket || this.socket.closed) { this.socket = undefined; this._listen(); }
    if (this.queued) { this.queued = false; this._beat(); return; }
    this.timer = setTimeout(() => this._beat(), this.heartbeatMs);
  }

  private _goIdle(): void {
    this.state = "idle";
    this.present = [];
    this._leave();
  }

  private _resume(): void {
    if (!this.activeId || this.state !== "idle") return;
    this.state = "starting";
    this.needsJoin = true;
    this._beat();
  }

  private _leave(): void {
    if (this.activeId && this.joined) this.api.leavePresence(this.activeId, this.sessionKey).subscribe({ error: () => undefined });
    this.joined = false;
  }

  private _stop(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
    this.inFlight?.unsubscribe();
    this.inFlight = undefined;
    this.queued = false;
    this._leave();
    this.activeId = undefined;
  }

  private _listen(): void {
    if (this.socket) return;
    this.socket = this.ws.getSubscription({
      next: (message: SocketData) => {
        if (message?.event === SocketEventTypeEnum.CollaborationActivity && Number(message.data?.data?.formGroupId) === this.activeId) this.zone.run(() => this.refresh());
      },
      error: () => undefined,
    });
  }
}
